import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Trash, Prohibit, MagnifyingGlass, Warning, ClockCounterClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Owner-only cross-tenant user management. Lists every non-superuser account,
 * with checkbox selection, active/ended filter, tenant filter, and one-click
 * bulk DELETE / bulk END-DATE across as many tenants as needed. Also exposes
 * a `HISTORY` tab so the Owner can review who was cleared and when.
 */
export default function BulkUsersModal({ open, onClose, onDone }) {
  const [tab, setTab] = useState("users");
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("active");
  const [tenantId, setTenantId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (active) params.set("active", active);
      if (tenantId) params.set("tenant_id", tenantId);
      const { data } = await api.get(`/platform/users?${params.toString()}`);
      setRows(data.items || []);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [q, active, tenantId]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/bulk-cleanup-history?limit=50");
      setHistory(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (tab === "users") load();
    else loadHistory();
  }, [open, tab, load, loadHistory]);

  if (!open) return null;

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently DELETE ${selected.size} user account(s) across their tenants?\n\nAudit trail is preserved. Superusers and your own account are silently skipped.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/users/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted_count} · skipped ${data.skipped_count}`);
      await load();
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };
  const bulkEndDate = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`End-date ${selected.size} user account(s)?\n\nThey will be blocked from signing in immediately but their records remain. Superusers and yourself are silently skipped.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/users/bulk-end-date", { ids: Array.from(selected) });
      toast.success(`End-dated ${data.ended_count} · skipped ${data.skipped_count}`);
      await load();
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk end-date failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkReactivate = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Reactivate ${selected.size} end-dated user account(s)?\n\nThey will be able to sign in again immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/users/bulk-reactivate", { ids: Array.from(selected) });
      const preview = (data.reactivated || []).slice(0, 5).map((r) => r.email).join(", ");
      const remainder = (data.reactivated_count || 0) - Math.min(5, data.reactivated_count || 0);
      const description = preview + (remainder > 0 ? ` (+${remainder} more)` : "");
      toast.success(`Reactivated ${data.reactivated_count} · skipped ${data.skipped_count}`, {
        description: description || undefined,
        duration: 8000,
      });
      await load();
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk reactivate failed");
    } finally {
      setBusy(false);
    }
  };

  const someSelected = selected.size > 0;
  const allSelected = rows.length > 0 && selected.size === rows.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-6 px-4"
      data-testid="bulk-users-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-950 w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF3E00]">SMARTERP / OWNER TOOL</div>
            <div className="font-display text-lg font-black tracking-tight">Bulk user cleanup</div>
          </div>
          <button onClick={onClose} data-testid="bulk-users-close" className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200 bg-zinc-50 px-5 flex items-center gap-1">
          <button
            onClick={() => setTab("users")}
            data-testid="bulk-tab-users"
            className={`px-3 py-2.5 text-[10px] font-mono tracking-[0.15em] uppercase border-b-2 -mb-px ${tab === "users" ? "border-[#FF3E00] text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}
          >
            Users
          </button>
          <button
            onClick={() => setTab("history")}
            data-testid="bulk-tab-history"
            className={`px-3 py-2.5 text-[10px] font-mono tracking-[0.15em] uppercase border-b-2 -mb-px flex items-center gap-1.5 ${tab === "history" ? "border-[#FF3E00] text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}
          >
            <ClockCounterClockwise size={11} /> Cleanup History
          </button>
          {tab === "history" && (
            <a
              href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/platform/bulk-cleanup-history.csv`}
              data-testid="bulk-history-export-csv"
              className="ml-auto font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-950 px-3 py-1.5 my-1 hover:bg-zinc-950 hover:text-white transition-colors"
            >
              Export CSV
            </a>
          )}
        </div>

        {tab === "users" && (<>
        {/* Filters + Actions */}
        <div className="border-b border-zinc-200 px-5 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-zinc-300 px-2 py-1 flex-1 min-w-[220px]">
            <MagnifyingGlass size={12} className="text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="search email / name…"
              data-testid="bulk-users-search"
              className="flex-1 outline-none text-sm font-mono"
            />
          </div>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value)}
            data-testid="bulk-users-status-filter"
            className="border border-zinc-300 px-2 py-1.5 text-xs font-mono"
          >
            <option value="active">Active only</option>
            <option value="ended">End-dated only</option>
            <option value="all">All statuses</option>
          </select>
          <input
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="tenant_id (optional)"
            data-testid="bulk-users-tenant-filter"
            className="border border-zinc-300 px-2 py-1.5 text-xs font-mono w-56"
          />
          <button
            onClick={load}
            data-testid="bulk-users-apply-btn"
            className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white"
          >
            APPLY
          </button>
        </div>

        {/* Bulk action bar */}
        <div className="border-b border-zinc-200 px-5 py-2 flex items-center justify-between bg-zinc-50">
          <div className="text-xs font-mono text-zinc-600">
            {selected.size} selected · {rows.length} loaded
          </div>
          <div className="flex items-center gap-2">
            {active === "ended" && (
              <button
                onClick={bulkReactivate}
                disabled={!someSelected || busy}
                data-testid="bulk-reactivate-btn"
                className="font-mono text-[10px] tracking-[0.15em] uppercase border border-emerald-600 text-emerald-700 px-3 py-1.5 hover:bg-emerald-600 hover:text-white disabled:opacity-40 flex items-center gap-1"
              >
                REACTIVATE {selected.size || ""}
              </button>
            )}
            <button
              onClick={bulkEndDate}
              disabled={!someSelected || busy}
              data-testid="bulk-end-date-btn"
              className="font-mono text-[10px] tracking-[0.15em] uppercase border border-amber-600 text-amber-700 px-3 py-1.5 hover:bg-amber-600 hover:text-white disabled:opacity-40 flex items-center gap-1"
            >
              <Prohibit size={11} /> END-DATE {selected.size || ""}
            </button>
            <button
              onClick={bulkDelete}
              disabled={!someSelected || busy}
              data-testid="bulk-delete-btn"
              className="font-mono text-[10px] tracking-[0.15em] uppercase border border-[#D0021B] text-[#D0021B] px-3 py-1.5 hover:bg-[#D0021B] hover:text-white disabled:opacity-40 flex items-center gap-1"
            >
              <Trash size={11} /> DELETE {selected.size || ""}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center font-mono text-xs text-zinc-400 pulse-dot">LOADING…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center font-mono text-xs text-zinc-400">NO USERS MATCH</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
                <tr className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500">
                  <th className="px-3 py-2 w-8 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      data-testid="bulk-users-select-all"
                    />
                  </th>
                  <th className="text-left px-3 py-2">Email / Name</th>
                  <th className="text-left px-3 py-2">Tenant</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const isEnded = u.active === false || Boolean(u.end_date);
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-zinc-100 hover:bg-zinc-50 ${selected.has(u.id) ? "bg-amber-50/50" : ""}`}
                      data-testid={`bulk-user-row-${u.id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(u.id)}
                          onChange={() => toggle(u.id)}
                          data-testid={`bulk-user-checkbox-${u.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-sm">{u.name || "—"}</div>
                        <div className="text-[11px] font-mono text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{u.tenant_name || "—"}</div>
                        <div className="font-mono text-[9px] text-zinc-400">{u.tenant_id}</div>
                        {u.tenant_ended && (
                          <span className="inline-flex items-center gap-1 mt-0.5 font-mono text-[9px] text-[#D0021B]"><Warning size={9} /> tenant closed</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 uppercase">
                          {u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {isEnded ? (
                          <span className="font-mono text-[10px] bg-[#D0021B] text-white px-1.5 py-0.5">END-DATED{u.end_date ? ` · ${u.end_date}` : ""}</span>
                        ) : (
                          <span className="font-mono text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5">ACTIVE</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        </>)}

        {tab === "history" && (
          <div className="flex-1 overflow-y-auto p-5" data-testid="bulk-cleanup-history-panel">
            {loading ? (
              <div className="py-10 text-center font-mono text-xs text-zinc-400 pulse-dot">LOADING HISTORY…</div>
            ) : history.length === 0 ? (
              <div className="py-10 text-center font-mono text-xs text-zinc-400">
                NO BULK CLEANUP RUNS YET
                <div className="mt-1 text-zinc-500 normal-case tracking-normal">Every bulk end-date and delete is logged here once you run one.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((h) => {
                  const isDelete = h.action === "bulk-delete";
                  const isExpanded = expandedRun === h.id;
                  return (
                    <div key={h.id} className="border border-zinc-200" data-testid={`history-run-${h.id}`}>
                      <button
                        onClick={() => setExpandedRun(isExpanded ? null : h.id)}
                        className="w-full flex flex-wrap items-center gap-3 px-3 py-2.5 bg-white hover:bg-zinc-50 text-left"
                      >
                        <span className={`font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 ${isDelete ? "bg-[#D0021B] text-white" : "bg-amber-500 text-white"}`}>
                          {isDelete ? "DELETE" : "END-DATE"}
                        </span>
                        <span className="text-xs font-bold text-zinc-950 tnum">{h.affected_count}</span>
                        <span className="text-[10px] font-mono text-zinc-500">affected · {h.skipped_count} skipped</span>
                        <span className="text-[11px] text-zinc-600 flex-1 truncate">by {h.run_by}</span>
                        <span className="text-[10px] font-mono text-zinc-500 tnum">
                          {h.run_at ? new Date(h.run_at).toLocaleString() : "—"}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="font-mono text-[10px] tracking-wider uppercase text-zinc-500 mb-1">Affected ({h.affected_count})</div>
                            {h.affected.length === 0 ? (
                              <div className="text-[11px] text-zinc-400">—</div>
                            ) : (
                              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                                {h.affected.map((a) => (
                                  <li key={a.user_id || a.email} className="text-[11px] font-mono text-zinc-700">
                                    {a.email} <span className="text-zinc-400">· {a.tenant_id}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <div className="font-mono text-[10px] tracking-wider uppercase text-zinc-500 mb-1">Skipped ({h.skipped_count})</div>
                            {h.skipped.length === 0 ? (
                              <div className="text-[11px] text-zinc-400">—</div>
                            ) : (
                              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                                {h.skipped.map((s) => (
                                  <li key={s.user_id || s.email || s.reason} className="text-[11px] font-mono text-zinc-700">
                                    {s.email || s.user_id} <span className="text-zinc-400">· {s.reason}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
